// Starts the card-first trial checkout (PANDA-2341) from any surface that
// opens the wall — the in-build gate, the credit chip, the workspace menu,
// Settings → Usage. One implementation so every door behaves the same way.
import { toast } from "sonner";
import {
  isCustomCheckoutEnabled,
  customCheckoutHref,
} from "@/lib/billing/checkout-mode";

/**
 * Resolves only when the redirect did NOT happen, so the caller's button can
 * come back — a wall whose only control stays disabled is a dead end
 * (PANDA-2342).
 */
export async function startCardFirstTrialCheckout(
  entrypoint: string,
  /**
   * Where checkout should land the buyer. Defaults to the page they clicked
   * from, which is right for every in-app surface — but not for the /start
   * landing, where "back to /start" would be a dead end (PANDA-2375).
   */
  returnTo?: string,
): Promise<void> {
  const dest =
    returnTo ?? `${window.location.pathname}${window.location.search}`;

  // Hand off to our own checkout page when it is on, the same as every other
  // door to a purchase. This one was minting the trial here and then following
  // `url` straight out to the processor, so the card-first wall was reaching
  // the hosted page even on a build where the embed was live — /checkout mints
  // it instead, from the identical parameters.
  if (isCustomCheckoutEnabled()) {
    window.location.assign(
      customCheckoutHref({
        type: "trial",
        surface: "t5_credit_gate",
        entrypoint,
        returnTo: dest,
      }),
    );
    return;
  }

  try {
    const res = await fetch("/api/billing/trial/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        // The in-build credit gate surface: already exempt from the offer
        // deadline and the anti-nag cap, which is right for a wall that every
        // one of these doors opens.
        surface: "t5_credit_gate",
        entrypoint,
        returnTo: dest,
      }),
    });
    const data = await res.json().catch(() => null);
    if (data?.url) {
      window.location.assign(data.url as string);
      return;
    }
    console.error("[card-first] trial start refused", {
      status: res.status,
      code: data?.code,
    });
    toast.error("We couldn't open checkout just now. Please try again in a moment.");
  } catch (err) {
    console.error("[card-first] trial start failed", err);
    toast.error("Network problem — check your connection and try again.");
  }
}
